import { useState, useMemo, useEffect } from 'react';
import { toaster } from '@welbe/design-system';
import { CustomSelectField } from 'App/Components/CustomSelect/CustomSelectField';
import { EmployeeSection } from '../../components/EmployeeSection';
import { useEmployee } from '../../Employee.hooks';
import { MEXICO_CODE, NON_VALUE_RESIDENCE } from '../../Employee.constants';
import { Controller } from 'react-hook-form';
import {
	getMunicipalitiesCatalog,
	getLocationsCatalog,
} from 'services/services';

type CatalogOption = { id: string; name: string };

const SPECIAL_ENTITY_IDS = ['99', '0', '00'];
const SPECIAL_MUNICIPALITY_IDS = ['998', '999'];

export const Residence = ({
	isEdition,
	errors,
	disabled,
}: {
	isEdition?: boolean;
	errors: string[];
	disabled?: boolean;
}) => {
	const {
		employeeForm,
		methods,
		countries,
		loadingCountries,
		federalEntities,
		loadingFederalEntities,
	} = useEmployee();
	const { control, setValue, watch } = methods;

	const countryValue = watch('siresProfile.regionalInfo.country');
	const federalEntityValue = watch('siresProfile.regionalInfo.federalEntity');
	const municipalityValue = watch('siresProfile.regionalInfo.municipality');
	const locationValue = watch('siresProfile.regionalInfo.location');

	const isMexico = String(countryValue ?? '') === MEXICO_CODE;
	const entityStr = String(federalEntityValue ?? '');
	const munStr = String(municipalityValue ?? '');

	const isSpecialEntity = isMexico && SPECIAL_ENTITY_IDS.includes(entityStr);
	const isSpecialMunicipality = SPECIAL_MUNICIPALITY_IDS.includes(munStr);

	const [municipalities, setMunicipalities] = useState<CatalogOption[]>([]);
	const [localities, setLocalities] = useState<CatalogOption[]>([]);
	const [loadingMunicipalities, setLoadingMunicipalities] = useState(false);
	const [loadingLocalities, setLoadingLocalities] = useState(false);

	// Cascade: entity → municipality + location (only in Mexico)
	useEffect(() => {
		if (!isMexico) return;
		if (entityStr === '99') {
			setValue('siresProfile.regionalInfo.municipality', 998);
			setValue('siresProfile.regionalInfo.location', 9998);
		} else if (entityStr === '0' || entityStr === '00') {
			setValue('siresProfile.regionalInfo.municipality', 999);
			setValue('siresProfile.regionalInfo.location', 9999);
		}
	}, [entityStr, isMexico, setValue]);

	// Cascade: municipality → location (only when entity is not special)
	useEffect(() => {
		if (!isMexico || isSpecialEntity) return;
		if (munStr === '998') {
			setValue('siresProfile.regionalInfo.location', 9998);
		} else if (munStr === '999') {
			setValue('siresProfile.regionalInfo.location', 9999);
		}
	}, [munStr, isMexico, isSpecialEntity, setValue]);

	// Sync localities when location is auto-set so CustomSelect can show the label
	useEffect(() => {
		const locStr = String(locationValue ?? '');
		const isAutoLocation =
			!isMexico || isSpecialEntity || isSpecialMunicipality;
		if (!isAutoLocation || !locStr) return;
		const nonValueItem = NON_VALUE_RESIDENCE.find((x) => x.id === locStr);
		if (nonValueItem) {
			setLocalities([{ id: nonValueItem.id, name: nonValueItem.name }]);
		}
	}, [locationValue, isMexico, isSpecialEntity, isSpecialMunicipality]);

	const effectiveFederalEntityOptions = useMemo(
		() =>
			!isMexico
				? NON_VALUE_RESIDENCE.filter((x) => x.id === '88')
				: federalEntities,
		[isMexico, federalEntities],
	);

	const effectiveMunicipalityOptions = useMemo(() => {
		if (!isMexico) return NON_VALUE_RESIDENCE.filter((x) => x.id === '998');
		if (isSpecialEntity) {
			const autoId = entityStr === '99' ? '998' : '999';
			return NON_VALUE_RESIDENCE.filter((x) => x.id === autoId);
		}
		return municipalities;
	}, [isMexico, isSpecialEntity, entityStr, municipalities]);

	const isMunicipalityDisabled = !isMexico || isSpecialEntity;
	const isLocationDisabled =
		!isMexico || isSpecialEntity || isSpecialMunicipality;

	const fetchMunicipalities = async (code: number) => {
		try {
			setLoadingMunicipalities(true);
			const { data } = await getMunicipalitiesCatalog(code);
			setMunicipalities(
				data.content.municipalities.map(
					(item: { code: string; name: string }) => ({
						id: String(item.code),
						name: item.name,
					}),
				),
			);
		} catch (error) {
			toaster.create({
				type: 'error',
				title: 'Hubo un error al buscar los municipios',
			});
		} finally {
			setLoadingMunicipalities(false);
		}
	};

	const fetchLocalities = async (code: number, federalEntity: number) => {
		try {
			setLoadingLocalities(true);
			const { data } = await getLocationsCatalog(code, federalEntity);
			setLocalities(
				data.content.locations.map((item: { code: string; name: string }) => ({
					id: String(item.code),
					name: item.name,
				})),
			);
			await new Promise((resolve) => setTimeout(resolve, 500));
		} catch (error) {
			toaster.create({
				type: 'error',
				title: 'Hubo un error al buscar las localidades',
			});
		} finally {
			setLoadingLocalities(false);
		}
	};

	useEffect(() => {
		if (isEdition && employeeForm?.siresProfile?.regionalInfo?.municipality) {
			fetchMunicipalities(
				Number(employeeForm?.siresProfile?.regionalInfo?.federalEntity),
			);
		}

		if (isEdition && employeeForm?.siresProfile?.regionalInfo?.location) {
			fetchLocalities(
				Number(employeeForm?.siresProfile?.regionalInfo?.municipality),
				Number(employeeForm?.siresProfile?.regionalInfo?.federalEntity),
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<EmployeeSection
			id="residence"
			title="Residencia"
			description="Esta información se registra únicamente para mantener registro estadístico sobre el control de enfermedades y contagios."
		>
			<Controller
				name="siresProfile.regionalInfo.country"
				control={control}
				render={({ field }) => (
					<CustomSelectField
						label="Pais de nacimiento"
						required
						readOnly={disabled}
						invalid={errors.includes('siresProfile') && !field.value}
						options={countries}
						value={field.value != null ? String(field.value) : ''}
						onChange={(id) => {
							const isNonMexico = id !== MEXICO_CODE;
							setValue(
								'siresProfile.regionalInfo.federalEntity',
								isNonMexico ? 88 : undefined,
							);
							setValue(
								'siresProfile.regionalInfo.municipality',
								isNonMexico ? 998 : undefined,
							);
							setValue(
								'siresProfile.regionalInfo.location',
								isNonMexico ? 9998 : undefined,
							);
							setMunicipalities([]);
							setLocalities([]);
							field.onChange(Number(id));
						}}
						placeholder="Seleccione una opción"
						loading={loadingCountries}
					/>
				)}
			/>

			<Controller
				name="siresProfile.regionalInfo.federalEntity"
				control={control}
				render={({ field }) => (
					<CustomSelectField
						label="Entidad de residencia"
						required
						disabled={!isMexico}
						readOnly={disabled}
						invalid={errors.includes('siresProfile') && !field.value}
						options={effectiveFederalEntityOptions}
						value={field.value != null ? String(field.value) : ''}
						onChange={(id) => {
							setValue('siresProfile.regionalInfo.municipality', undefined);
							setValue('siresProfile.regionalInfo.location', undefined);
							setMunicipalities([]);
							setLocalities([]);
							if (!SPECIAL_ENTITY_IDS.includes(id)) {
								fetchMunicipalities(Number(id));
							}
							field.onChange(Number(id));
						}}
						placeholder="Seleccione una opción"
						loading={loadingFederalEntities}
					/>
				)}
			/>

			<Controller
				name="siresProfile.regionalInfo.municipality"
				control={control}
				render={({ field }) => (
					<CustomSelectField
						label="Municipio de residencia"
						required
						disabled={isMunicipalityDisabled}
						readOnly={disabled}
						invalid={errors.includes('siresProfile') && !field.value}
						options={effectiveMunicipalityOptions}
						value={field.value != null ? String(field.value) : ''}
						onChange={(id) => {
							setValue('siresProfile.regionalInfo.location', undefined);
							setLocalities([]);
							if (!SPECIAL_MUNICIPALITY_IDS.includes(id)) {
								fetchLocalities(Number(id), Number(entityStr));
							}
							field.onChange(Number(id));
						}}
						placeholder="Seleccione una opción"
						loading={loadingMunicipalities}
					/>
				)}
			/>

			<Controller
				name="siresProfile.regionalInfo.location"
				control={control}
				render={({ field }) => (
					<CustomSelectField
						label="Localidad de residencia"
						required
						disabled={isLocationDisabled}
						readOnly={disabled}
						invalid={errors.includes('siresProfile') && !field.value}
						options={localities}
						value={field.value != null ? String(field.value) : ''}
						onChange={(id) => field.onChange(Number(id))}
						placeholder="Selecciona una localidad"
						loading={loadingLocalities}
					/>
				)}
			/>
		</EmployeeSection>
	);
};
